/**
 * Searchable selection with a panel of options below the field, single or
 * multiple (the `field-selector` selects of the dialogs and the list filters).
 * Single: the picked label sits in the field, a cross clears it. Multiple: the
 * picked items are chips inside the field, each with its own cross, and a
 * cross clears them all. Fully keyboard-operable: the input is a combobox over
 * a listbox, a click on the field toggles the panel, typing or arrows open
 * it, Enter picks, Backspace drops the last pick, Escape closes.
 */
import { useEffect, useId, useRef, useState, type KeyboardEvent } from 'react';

import type { SelectOption } from './select-options';

interface CommonProps {
  id?: string;
  name?: string;
  options: readonly SelectOption[];
  placeholder?: string;
  /** Picked options leave the panel (ng-select `hideSelected`). */
  hideSelected?: boolean;
  disabled?: boolean;
  'aria-label'?: string;
  'aria-labelledby'?: string;
  testId?: string | undefined;
}

export interface SingleSelectProps extends CommonProps {
  multiple?: false;
  /** Empty string when nothing is picked. */
  value: string;
  onChange: (value: string) => void;
}

export interface MultiSelectProps extends CommonProps {
  multiple: true;
  value: readonly string[];
  onChange: (values: string[]) => void;
}

export type SelectProps = SingleSelectProps | MultiSelectProps;

const pickedValues = (props: SelectProps): readonly string[] => {
  if (props.multiple) return props.value;
  return props.value ? [props.value] : [];
};

export function Select(props: Readonly<SelectProps>) {
  const {
    id,
    name,
    options,
    placeholder,
    hideSelected = false,
    disabled = false,
    'aria-label': ariaLabel,
    'aria-labelledby': labelledBy,
    testId,
  } = props;
  const multiple = props.multiple === true;
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState('');
  const [marked, setMarked] = useState(0);
  const rootRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const listId = useId();

  const values = pickedValues(props);
  const selected = new Set(values);
  const term = search.trim().toLowerCase();
  const visible = options.filter(
    (option) =>
      (!hideSelected || !selected.has(option.value)) &&
      (!term || option.label.toLowerCase().includes(term)),
  );
  const picked = values
    .map((item) => options.find((option) => option.value === item))
    .filter((option): option is SelectOption => !!option);

  useEffect(() => {
    if (!open) return;
    const onPointerDown = (event: MouseEvent) => {
      if (!rootRef.current?.contains(event.target as Node)) setOpen(false);
    };
    document.addEventListener('mousedown', onPointerDown);
    return () => document.removeEventListener('mousedown', onPointerDown);
  }, [open]);

  const commit = (next: string[]) => {
    if (props.multiple) props.onChange(next);
    else props.onChange(next[0] ?? '');
  };

  const toggle = (option: SelectOption) => {
    if (multiple) {
      commit(
        selected.has(option.value)
          ? values.filter((item) => item !== option.value)
          : [...values, option.value],
      );
    } else {
      commit([option.value]);
      setOpen(false);
    }
    setSearch('');
    inputRef.current?.focus();
  };

  const clear = () => {
    commit([]);
    setSearch('');
    inputRef.current?.focus();
  };

  const onKeyDown = (event: KeyboardEvent<HTMLInputElement>) => {
    if (event.key === 'ArrowDown') {
      event.preventDefault();
      setOpen(true);
      setMarked((index) => Math.min(index + 1, visible.length - 1));
    } else if (event.key === 'ArrowUp') {
      event.preventDefault();
      setMarked((index) => Math.max(index - 1, 0));
    } else if (event.key === 'Enter') {
      event.preventDefault();
      const option = visible[marked];
      if (open && option) toggle(option);
      else setOpen(true);
    } else if (event.key === 'Escape') {
      if (open) event.preventDefault();
      setOpen(false);
    } else if (event.key === 'Backspace' && !search && values.length) {
      commit(values.slice(0, -1));
    }
  };

  const markedId = (index: number) => `${listId}-${index}`;
  const single = multiple ? undefined : picked[0];
  const showPlaceholder = picked.length === 0;
  const subId = (suffix: string) =>
    testId ? `${testId}-${suffix}` : undefined;

  return (
    <div
      ref={rootRef}
      className={`combo ${multiple ? 'multiple' : 'single'} ${picked.length ? 'filled' : ''} ${open ? 'open' : ''} ${disabled ? 'disabled' : ''}`}
    >
      <div
        className="combo-box"
        onClick={(event) => {
          if ((event.target as HTMLElement).closest('button')) return;
          inputRef.current?.focus();
          setOpen((state) => !state);
        }}
        role="presentation"
      >
        {multiple &&
          picked.map((option) => (
            <span
              key={option.value}
              className="combo-chip"
              data-testid={subId('chip')}
              data-value={option.value}
            >
              <button
                type="button"
                className="combo-chip-remove"
                aria-label={`Remove ${option.label}`}
                disabled={disabled}
                onClick={() => toggle(option)}
                data-testid={subId('chip-remove-button')}
              >
                ×
              </button>
              <span className="combo-chip-label">{option.label}</span>
            </span>
          ))}
        {single && !search && (
          <span className="combo-value">{single.label}</span>
        )}
        <input
          ref={inputRef}
          id={id}
          name={name}
          type="text"
          role="combobox"
          autoComplete="off"
          aria-expanded={open}
          aria-controls={listId}
          aria-autocomplete="list"
          aria-label={ariaLabel}
          aria-labelledby={labelledBy}
          aria-activedescendant={
            open && visible[marked] ? markedId(marked) : undefined
          }
          className="combo-input"
          placeholder={showPlaceholder ? placeholder : ''}
          value={search}
          disabled={disabled}
          onChange={(event) => {
            setSearch(event.target.value);
            setMarked(0);
            setOpen(true);
          }}
          onKeyDown={onKeyDown}
          data-testid={testId}
        />
        {values.length > 0 && !disabled && (
          <button
            type="button"
            className="combo-clear"
            aria-label={multiple ? 'Clear all' : 'Clear'}
            onClick={clear}
            data-testid={subId('clear-button')}
          >
            ×
          </button>
        )}
      </div>
      {open && (
        <ul
          id={listId}
          role="listbox"
          aria-multiselectable={multiple || undefined}
          className="combo-panel"
          data-testid={subId('panel')}
        >
          {visible.length === 0 && (
            <li className="combo-empty">No items found</li>
          )}
          {visible.map((option, index) => (
            <li
              key={option.value}
              id={markedId(index)}
              role="option"
              aria-selected={selected.has(option.value)}
              className={`combo-option ${index === marked ? 'marked' : ''} ${selected.has(option.value) ? 'selected' : ''}`}
              onMouseDown={(event) => event.preventDefault()}
              onMouseEnter={() => setMarked(index)}
              onClick={() => toggle(option)}
              onKeyDown={(event) => {
                if (event.key === 'Enter' || event.key === ' ') toggle(option);
              }}
              data-testid={subId('option')}
              data-value={option.value}
            >
              {option.label}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
